/**
 * CREATION CARD
 * 
 * Displays a creation in grid or list view
 */

import React from 'react';
import { Store, Clock, Palette, DollarSign } from 'lucide-react';
import { AppIcons } from '../../constants/icons';
import type { Creation } from '../../types/creationTypes';

interface CreationCardProps {
  creation: Creation;
  viewMode: 'grid' | 'list';
  onClick: () => void;
}

const CreationCard: React.FC<CreationCardProps> = ({ creation, viewMode, onClick }) => {
  // Get primary photo
  const primaryPhoto = creation.photos?.find(p => p.isDefault) || creation.photos?.[0];
  const photoUrl = primaryPhoto?.url;

// Determine the type to display (creationType takes priority, then fallback to marketplaceCategory for old data)
  const getDisplayType = (): string => {
    // If creationType is set, not empty, and not 'other', use it
    if (creation.creationType && creation.creationType.trim() !== '' && creation.creationType !== 'other') {
      return creation.creationType;
    }
    // If it's 'other' but explicitly a marketplace item, keep it as 'other'
    if (creation.creationType === 'other' && creation.listedOnMarketplace) {
      return 'other';
    }
    // Fallback for old data using marketplaceCategory
    if (creation.marketplaceCategory === 'pressons') return 'press-ons';
    if (creation.marketplaceCategory === 'jewelry') return 'jewelry';
    // Default to 'mani' for everything else (including 'other' for non-marketplace items)
    return 'mani';
  };

  const displayType = getDisplayType();

  // Type badge color
  const getTypeStyle = () => {
    switch (displayType) {
      case 'mani':
        return 'bg-pink-100 text-pink-700';
      case 'pedi':
        return 'bg-rose-100 text-rose-700';
      case 'nail-art':
        return 'bg-violet-100 text-violet-700';
      case 'practice':
        return 'bg-slate-100 text-slate-700';
      case 'press-ons':
        return 'bg-purple-100 text-purple-700';
      case 'jewelry':
        return 'bg-amber-100 text-amber-700';
      case 'other':
        return 'bg-teal-100 text-teal-700';
      default:
        return 'bg-pink-100 text-pink-700';
    }
  };

  const getTypeLabel = () => {
    switch (displayType) {
      case 'mani': return 'Mani';
      case 'pedi': return 'Pedi';
      case 'nail-art': return 'Nail Art';
      case 'practice': return 'Practice';
      case 'press-ons': return 'Press-Ons';
      case 'jewelry': return 'Jewelry';
      case 'other': return 'Other';
      default: return 'Mani';
    }
  };

  // Status badge
  const getStatusBadge = () => {
    if (!creation.listedOnMarketplace) return null;
    
    switch (creation.marketplaceStatus) {
      case 'pending':
        return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Pending</span>;
      case 'sold':
        return <span className="px-2 py-0.5 border-2 border-gray-300 text-light-secondary text-xs rounded-full">Sold</span>;
      default:
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Available</span>;
    }
  };

  if (viewMode === 'list') {
    return (
      <div
        onClick={onClick}
        className="card-light hover:shadow-2xl transition-all cursor-pointer overflow-hidden flex"
      >
        {/* Photo */}
        <div className="w-32 h-32 flex-shrink-0">
          {photoUrl ? (
            <img src={photoUrl} alt={creation.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-fuchsia-200 to-purple-200 flex items-center justify-center">
              <AppIcons.Creation className="w-10 h-10 text-purple-600" aria-label="Creation" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 p-4 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getTypeStyle()}`}>
                {getTypeLabel()}
              </span>
              {getStatusBadge()}
              {creation.listedOnMarketplace && (
                <Store className="icon-sm text-green-500" />
              )}
            </div>
            <h3 className="font-bold text-light-primary font-body">
              {creation.title}
            </h3>
            <p className="text-sm text-light-muted line-clamp-1">{creation.description}</p>
          </div>

          <div className="flex items-center gap-4 text-sm text-light-muted">
            <span className="flex items-center gap-1">
              <Palette className="icon-sm" />
              {creation.itemsUsed?.length || 0} items
            </span>
            {creation.price && (
              <span className="flex items-center gap-1 text-green-600 font-medium">
                <DollarSign className="icon-sm" />
                {creation.price.toFixed(2)}
              </span>
            )}
            {creation.dateCreated && (
              <span className="flex items-center gap-1">
                <Clock className="icon-sm" />
                {new Date(creation.dateCreated).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all cursor-pointer overflow-hidden group"
    >
      {/* Photo */}
      <div className="aspect-square relative overflow-hidden">
        {photoUrl ? (
          <img 
            src={photoUrl} 
            alt={creation.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-fuchsia-200 to-purple-200 flex items-center justify-center">
            <AppIcons.Creation className="w-16 h-16 text-purple-600" aria-label="Creation" />
          </div>
        )}

        {/* Overlay badges */}
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
          <span className={`px-2 py-1 text-xs font-bold rounded-full shadow ${getTypeStyle()}`}>
            {getTypeLabel()}
          </span>
          {creation.listedOnMarketplace && (
            <div className="bg-green-500 text-white p-1.5 rounded-full shadow">
              <Store className="icon-sm" />
            </div>
          )}
        </div>

        {/* Price badge */}
        {creation.price && creation.listedOnMarketplace && (
          <div className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full shadow">
            <span className="text-green-600 font-bold text-sm">${creation.price.toFixed(2)}</span>
          </div>
        )}

        {/* Status overlay for sold/pending */}
        {creation.marketplaceStatus === 'sold' && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-white text-light-primary px-4 py-2 rounded-full font-bold text-lg">SOLD</span>
          </div>
        )}
        {creation.marketplaceStatus === 'pending' && (
          <div className="absolute inset-0 bg-yellow-500/30 flex items-center justify-center">
            <span className="bg-yellow-500 text-white px-4 py-2 rounded-full font-bold">PENDING</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <h3 className="font-bold text-light-primary truncate font-body">
          {creation.title}
        </h3>
        <p className="text-sm text-light-muted line-clamp-2 mt-1">{creation.description}</p>
        
        <div className="flex items-center justify-between mt-3 text-sm text-light-muted">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Palette className="icon-sm" />
              {creation.itemsUsed?.length || 0}
            </span>
            {creation.dateCreated && (
              <span className="flex items-center gap-1">
                <Clock className="icon-sm" />
                {new Date(creation.dateCreated).toLocaleDateString()}
              </span>
            )}
          </div>
          {getStatusBadge()}
        </div>
      </div>
    </div>
  );
};

export default CreationCard;
